import React from "react";
import { Container } from "../../atoms/container/Container";
import { Text } from "../../atoms/text/Text";
import style from "./doctorConsult.module.scss";
import { Button, Flex, Typography } from "antd";
import { useDoctorConsult, ConsultationStage } from "../../../hooks/useDoctorConsult/UseDoctorConsult";
import { usePaymentStatus } from "../../../hooks/usePaymentStatus/usePaymentStatus";
import { PaymentButton } from "../paymentButton/PaymentButton";
import { MdOutlinePhoneInTalk, MdAccessTime } from "react-icons/md";
import { BsSendCheck } from "react-icons/bs";
import { RiUserHeartLine } from "react-icons/ri";
import { AiOutlineSend } from "react-icons/ai";
// @ts-ignore
import ReactFileReader from "react-file-reader";
import type { SseDetail } from "../../../redux/consultationSlice/consultationSlice";
import { AgentScript } from "../../atoms/agentScript/AgentScript";
import DoctorSelect from "../../atoms/doctorSelect/DoctorSelect";
import { DoctorCard } from "../../molecules/doctorCard/DoctorCard";

const { Text: TextAnt } = Typography;

const STAGES: { key: ConsultationStage; label: string; icon: React.ReactNode; desc: string }[] = [
  { key: "requested", label: "Requested", icon: <BsSendCheck />,          desc: "Request sent to doctor" },
  { key: "ongoing",   label: "Ongoing",   icon: <MdOutlinePhoneInTalk />, desc: "Consultation in progress" },
  { key: "completed", label: "Completed", icon: <RiUserHeartLine />,      desc: "Consultation finished" },
];

const stageIndex = (stage: ConsultationStage | null) =>
  STAGES.findIndex((s) => s.key === stage);

const toTrackerStage = (status: ConsultationStage | null): ConsultationStage | null => {
  if (!status) return null;
  if (["dispatched", "timeout", "rejected", "requested"].includes(status)) return "requested";
  if (["accepted", "ongoing", "call initiated", "call_initiated"].includes(status)) return "ongoing";
  if (status === "completed") return "completed";
  return null;
};

const getSearchingMessage = (detail: SseDetail): string => {
  const { status, doctor_index, total_doctors } = detail;
  const idx = doctor_index ?? "?";
  const total = total_doctors ?? "?";
  if (status === "dispatched") return `Connecting to doctor ${idx} of ${total}...`;
  if (status === "timeout")    return `Doctor didn't respond. Trying next doctor...`;
  if (status === "rejected")   return `Doctor is unavailable. Trying next doctor...`;
  return "";
};

export const DoctorConsult = () => {
  const {
    consultationMode,
    isRequestLoading,
    consultationStage,
    sseDetail,
    acceptedDetail,
    eventLog,
    sendConsultationRequest,
    sendDoctorConsultPaymentLink,
    getTicketStatus,
    getTicketData,
    uploading,
    isBtnLoading,
    handlePrescriptionDoc,
    handleViewCurrentDocument,
    handlePrescriptionSend,
    doctorRosters,
    handleDoctorChange,
    HandleDoctorRoasters,
    doctorData,
    doctorNumber,
    handleDoctorMobileNo,
    isSendRequestDisabled,
    pocNumber,
  } = useDoctorConsult();

  const hasPrescription = !!getTicketData?.doctorInfo?.clinicalNotes?.[0]?.url;

  const { paymentBtnStatus_Doctor, sendPaymentLink, refreshPaymentStatus, isRefreshing } = usePaymentStatus();
  const activeIdx = stageIndex(toTrackerStage(consultationStage));
  const isCancelled = consultationStage === "cancelled";
  const isCompleted = consultationStage === "completed";
  const searchingEvents = ["dispatched", "timeout", "rejected"];
  const isSearching = sseDetail && searchingEvents.includes(sseDetail.status);

  return (
    <div className={style.consultWrapper}>
      <div className={style.twoCol}>

        {/* ── LEFT: flow ── */}
        <div className={style.leftCol}>
          <Text text="Doctor Consultation" className={style.title} />

          <AgentScript text='"I am arranging a doctor consultation."' />

          <PaymentButton
            btnDisable={getTicketStatus === "completed"}
            component="Doctor-Consult"
            paymentBtnStatus={paymentBtnStatus_Doctor}
            style={style.link_btn}
            createPaymentLink={sendDoctorConsultPaymentLink}
            sendPaymentLink={() => sendPaymentLink("doctor-consultation")}
            onRefreshStatus={refreshPaymentStatus}
            isRefreshing={isRefreshing}
          />

          <DoctorSelect doctors={doctorRosters} onClick={HandleDoctorRoasters} onChange={handleDoctorChange} />

          {doctorData ? (
            <DoctorCard
              doctorName={`Dr. ${doctorData.doctorName}`}
              number={doctorData.mobileNo}
              startTime={doctorData.startTime}
              endTime={doctorData.endTime}
              patientNumber={getTicketData?.patient?.mobile || ""}
              pocNumber={pocNumber}
              emergencyContacts={[
                ...(getTicketData?.patient?.emergencyContacts || []),
                ...(getTicketData?.campusContacts || []),
              ]}
            />
          ) : (
            <div className={style.manualDoctorWrapper}>
              <Text text="OR" className={style.center} />
              <div className={style.input_box}>
                <input
                  placeholder="Doctor Mobile"
                  className={style.input}
                  value={doctorNumber}
                  onChange={handleDoctorMobileNo}
                  disabled={getTicketStatus === "completed"}
                />
              </div>
            </div>
          )}

          <Button
            className={style.sendRequestBtn}
            onClick={sendConsultationRequest}
            loading={isRequestLoading}
            disabled={isSendRequestDisabled}
          >
            Send Consultation Request
          </Button>

          <Container>
            {hasPrescription ? (
              <>
                <Button
                  loading={uploading}
                  style={{ marginTop: "10px" }}
                  onClick={handleViewCurrentDocument}
                >
                  View Document
                </Button>
                <Button
                  icon={<AiOutlineSend />}
                  style={{ marginLeft: "10px", marginTop: "10px" }}
                  disabled={
                    getTicketData?.doctorInfo?.clinicalNotes?.length && getTicketStatus !== "completed" ? false : true
                  }
                  loading={isBtnLoading}
                  onClick={handlePrescriptionSend}
                >
                  Send
                </Button>
              </>
            ) : (
              <ReactFileReader fileTypes={[".pdf"]} base64={true} multipleFiles={false} handleFiles={handlePrescriptionDoc}>
                <Flex align="center" gap={"1rem"} style={{ marginTop: "10px" }}>
                  <TextAnt className={style.warning} type="danger">Upload Only PDF</TextAnt>
                  <Button loading={uploading} className={style.meetingLinkBtn}>Upload Document</Button>
                </Flex>
              </ReactFileReader>
            )}
          </Container>
        </div>

        {/* ── DIVIDER ── */}
        <div className={style.divider} />

        {/* ── RIGHT: status ── */}
        {consultationMode === "video" && (
        <div className={style.rightCol}>
          <Text text="Consultation Status" className={style.progressTitle} />

          <div className={style.stageList}>
            {STAGES.map((stage, idx) => {
              const isDone   = consultationStage !== null && idx < activeIdx;
              const isActive = consultationStage !== null && idx === activeIdx;
              return (
                <React.Fragment key={stage.key}>
                  <div className={style.stageRow}>
                    <div className={`${style.stepNode} ${isDone ? style.stepDone : ""} ${isActive ? style.stepActive : ""} ${!isDone && !isActive ? style.stepPending : ""}`}>
                      <span className={style.stepIcon}>{stage.icon}</span>
                      {isActive && <span className={style.pulse} />}
                    </div>
                    <div className={style.stageText}>
                      <span className={`${style.stageLabel} ${isActive ? style.stageLabelActive : ""} ${isDone ? style.stageLabelDone : ""}`}>
                        {stage.label}
                      </span>
                      <span className={style.stageDesc}>{stage.desc}</span>
                    </div>
                    {isDone && <span className={style.doneBadge}>✓</span>}
                    {isActive && consultationStage !== "completed" && <span className={style.activeBadge}>Live</span>}
                    {isActive && consultationStage === "completed" && <span className={style.doneBadge}>✓</span>}
                  </div>
                  {idx < STAGES.length - 1 && (
                    <div className={`${style.vertConnector} ${isDone ? style.vertConnectorDone : ""}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {isCancelled && (
            <p className={style.cancelledMsg}>No doctor accepted the request</p>
          )}

          {!consultationStage && (
            <p className={style.noStatusHint}>Send a request to track status</p>
          )}
        </div>
        )}

      </div>

      {/* ── CALL PROGRESS DETAIL ── */}
      {sseDetail && consultationMode === "video" && (
        <div className={style.progressDetail}>
          <Text text="Call Progress" className={style.progressDetailTitle} />

          {isSearching && (
            <div className={style.progressMsg}>
              <span className={style.progressDot} />
              <span>{getSearchingMessage(sseDetail)}</span>
            </div>
          )}

          {(sseDetail?.status === "ongoing" || sseDetail?.status === "accepted" || sseDetail?.status === "call_initiated" || sseDetail?.status === "call initiated") && acceptedDetail && (
            <div className={style.doctorAcceptedCard}>
              <div className={style.doctorAcceptedHeader}>
                <span className={style.doctorAcceptedDot} />
                <span className={style.doctorAcceptedTitle}>
                  {sseDetail?.status === "call_initiated" || sseDetail?.status === "call initiated" ? "Call Initiated" : "Doctor Accepted"}
                </span>
                <span className={style.doctorAcceptedTime}>
                  {sseDetail?.status === "call_initiated" || sseDetail?.status === "call initiated" ? "Initiated at " : "Joined at "}
                  {acceptedDetail.accepted_at}
                </span>
              </div>
              <div className={style.doctorAcceptedInfo}>
                <span className={style.doctorAcceptedName}>{acceptedDetail.doctor_name || acceptedDetail.doctor_id}</span>
                {acceptedDetail.doctor_phone && (
                  <span className={style.doctorAcceptedPhone}>{acceptedDetail.doctor_phone}</span>
                )}
              </div>
            </div>
          )}

          {sseDetail?.status === "ongoing" && sseDetail.room_id && sseDetail.room_id !== "external" && (
            <div className={style.progressMsg}>
              <span className={`${style.progressDot} ${style.progressDotGreen}`} />
              <span>Call in progress &mdash; Room: <strong>{sseDetail.room_id}</strong></span>
            </div>
          )}

          {(sseDetail?.status === "call_initiated" || sseDetail?.status === "call initiated") && (
            <div className={style.progressMsg}>
              <span className={`${style.progressDot} ${style.progressDotGreen}`} />
              <span>Meeting link sent via WhatsApp</span>
            </div>
          )}

          {isCompleted && (
            <div className={style.progressMsg}>
              <MdAccessTime className={style.progressMsgIcon} />
              <span>
                Call completed
                {sseDetail?.call_duration_seconds !== undefined && (
                  <>
                    {" "}
                    &mdash; Duration:{" "}
                    <strong>
                      {Math.floor(sseDetail.call_duration_seconds / 60)}m {sseDetail.call_duration_seconds % 60}s
                    </strong>
                  </>
                )}
              </span>
            </div>
          )}

          {isCancelled && sseDetail.failed_doctors && sseDetail.failed_doctors.length > 0 && (
            <div className={style.auditTrail}>
              <span className={style.auditLabel}>Failed attempts:</span>
              {sseDetail.failed_doctors.map((d, i) => (
                <div key={i} className={style.auditRow}>
                  <span className={style.auditIndex}>{i + 1}</span>
                  <span className={style.auditDoctorId}>{d.doctor_name || d.doctor_id}</span>
                  <span className={style.auditReason}>
                    {d.reason === "timeout" ? "didn't respond" : "declined"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CONSULTATION LOG ── */}
      {eventLog.length > 0 && consultationMode === "video" && (
        <div className={style.consultLog}>
          <Text text="Consultation Log" className={style.consultLogTitle} />
          {eventLog.map((entry, i) => (
            <div key={i} className={style.consultLogRow}>
              <span className={style.consultLogTime}>{entry.logged_at}</span>
              <span className={`${style.consultLogBadge} ${style[`badge_${entry.status.replace(/\s+/g, '_')}`] || style[`badge_${entry.status}`] || style.badge_default}`}>
                {entry.status}
              </span>
              <span className={style.consultLogDesc}>
                {entry.doctor_name && <strong>{entry.doctor_name}</strong>}
                {entry.doctor_index && entry.total_doctors && (
                  <> &mdash; {entry.doctor_index} of {entry.total_doctors}</>
                )}
                {entry.call_duration_seconds !== undefined && (
                  <> &mdash; {Math.floor(entry.call_duration_seconds / 60)}m {entry.call_duration_seconds % 60}s</>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};
